// =======================
// src/paginas/secretaria/sre/FormularioSre.jsx
// =======================

import React, { useEffect, useRef, useState } from "react";

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Add,
  CheckCircleRounded,
  Close,
  EmailRounded,
  HelpOutlineRounded,
  HomeWorkRounded,
  InfoRounded,
  KeyRounded,
  LinkRounded,
  LocationCityRounded,
  LocationOnRounded,
  LockResetRounded,
  MapRounded,
  NumbersRounded,
  PhoneRounded,
  RemoveCircleOutline,
  ShieldRounded,
  SignpostRounded,
  VisibilityOffRounded,
  VisibilityRounded,
} from "@mui/icons-material";

import { useNavigate } from "react-router-dom";
import { api } from "@api/api";
import FeedbackInline from "@componentes/feedback/FeedbackInline";
import { BuildingsFillIcon } from "../../../componentes/ItemList";

import {
  botaoAcaoExcluir,
  botaoAdicionar,
  botaoCancelar,
  botaoSalvar,
} from "../../../estilos/botoes";

import { tamanhosModaisGlobais } from "../../../estilos/global";

// =========================================================
// ESTILOS
// =========================================================

const ESTILO_CAMPO_EDICAO = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40,
    borderRadius: 1.15,
    backgroundColor: "var(--sear-superficie-suave, #f4f6f8)",
    fontSize: tamanhosModaisGlobais.campo,
    color: "var(--sear-texto-secundario, #40546d)",

    "& fieldset": {
      borderColor: "var(--sear-borda, #dce4ec)",
    },

    "&:hover fieldset": {
      borderColor: "var(--sear-borda, #b7cde3)",
    },

    "&.Mui-focused fieldset": {
      borderColor: "var(--sear-borda-forte, #087cf0)",
    },
  },

  "& .MuiInputBase-input": {
    py: 1.05,
  },

  "& .MuiInputAdornment-root": {
    color: "var(--sear-azul, #087cf0)",
  },

  "& .MuiFormHelperText-root": {
    fontSize: tamanhosModaisGlobais.textoAuxiliar,
  },
};

const CAMPOS_ENDERECO = new Set([
  "cep",
  "state",
  "city",
  "neighborhood",
  "street",
  "number",
]);

// =========================================================
// ESTADOS INICIAIS
// =========================================================

const novoFormulario = () => ({
  name: "",
  email: "",
  phones: [""],
  site: "",
  cep: "",
  state: "",
  city: "",
  neighborhood: "",
  street: "",
  number: "",
  cities: [],
  active: true,
});

const novoFormularioSenha = () => ({
  novaSenha: "",
  confirmarNovaSenha: "",
});

const novoFormularioAcessoCriacao = () => ({
  senhaProvisoria: "",
  confirmarSenhaProvisoria: "",
});

// =========================================================
// FORMATAÇÕES
// =========================================================

function formatarCep(valor) {
  const numeros = String(valor || "")
    .replace(/\D/g, "")
    .slice(0, 8);

  return numeros.length > 5
    ? `${numeros.slice(0, 5)}-${numeros.slice(5)}`
    : numeros;
}

function formatarTelefone(valor) {
  const numeros = String(valor || "")
    .replace(/\D/g, "")
    .slice(0, 11);

  if (!numeros) {
    return "";
  }

  if (numeros.length <= 2) {
    return `(${numeros}`;
  }

  const local = numeros.slice(2);
  const corte = numeros.length > 10 ? 5 : 4;

  return (
    `(${numeros.slice(0, 2)}) ${local.slice(0, corte)}` +
    (local.length > corte ? `-${local.slice(corte)}` : "")
  );
}

function separarTelefones(valor) {
  return String(valor || "")
    .split(/\s*\/\s*|\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatarTelefoneExistente(valor) {
  return /^\d{10,11}$/.test(String(valor || "").replace(/\D/g, ""))
    ? formatarTelefone(valor)
    : valor;
}

function obterMensagemErro(data, fallback) {
  const mensagem = data?.message;

  if (Array.isArray(mensagem)) {
    return mensagem.join(" ");
  }

  return mensagem || fallback;
}

function normalizarSite(valor) {
  const site = String(valor || "").trim();

  if (!site) {
    return "";
  }

  return /^www\./i.test(site) ? `https://${site}` : site;
}

// =========================================================
// ENDEREÇO
// =========================================================

function montarEndereco(formulario) {
  const endereco =
    `${formulario.street.trim()}, ${formulario.number.trim()} - ` +
    `${formulario.neighborhood.trim()}, ${formulario.city.trim()}/` +
    `${formulario.state.trim().toUpperCase()} - CEP ${formulario.cep.trim()}`;

  const site = normalizarSite(formulario.site);

  return site ? `${endereco} | Site: ${site}` : endereco;
}

function lerEndereco(valor = "") {
  const partes = valor.match(
    /^(.*?),\s*([^,]+?) - (.*?),\s*([^/]+)\/([A-Za-z]{2}) - CEP (\d{5}-?\d{3})(?: \| Site: (.+))?$/,
  );

  if (!partes) {
    return null;
  }

  return {
    street: partes[1].trim(),
    number: partes[2].trim(),
    neighborhood: partes[3].trim(),
    city: partes[4].trim(),
    state: partes[5].toUpperCase(),
    cep: partes[6],
    site: partes[7] || "",
  };
}

// =========================================================
// COMPONENTE
// =========================================================

export default function FormularioSre({
  id,
  open = true,
  onClose,
  onCreated,
  onSaved,
}) {
  const navigate = useNavigate();
  const editando = id !== undefined && id !== null;

  // =========================================================
  // DADOS GERAIS
  // =========================================================

  const [formulario, setFormulario] = useState(novoFormulario);
  const [nomeDigitado, setNomeDigitado] = useState("");
  const [cidadesDisponiveis, setCidadesDisponiveis] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [dadosCarregados, setDadosCarregados] = useState(!editando);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [errosCampos, setErrosCampos] = useState({});
  const [avisosCampos, setAvisosCampos] = useState({});

  const validacaoDadosSreSeq = useRef({
    name: 0,
    email: 0,
  });

  const [enderecoBaseOriginal, setEnderecoBaseOriginal] = useState("");
  const [editandoEndereco, setEditandoEndereco] = useState(!editando);
  const [abaAtiva, setAbaAtiva] = useState(0);

  // =========================================================
  // EDIÇÃO — DADOS DE ACESSO
  // =========================================================

  const [acesso, setAcesso] = useState(null);
  const [acessoCarregado, setAcessoCarregado] = useState(false);
  const [carregandoAcesso, setCarregandoAcesso] = useState(false);
  const [erroAcesso, setErroAcesso] = useState("");
  const [sucessoAcesso, setSucessoAcesso] = useState("");

  const [formularioSenha, setFormularioSenha] = useState(novoFormularioSenha);
  const [errosSenha, setErrosSenha] = useState({});
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  const [exigirTrocaSenha, setExigirTrocaSenha] = useState(false);

  // =========================================================
  // CRIAÇÃO — DADOS DE ACESSO
  // =========================================================

  const [formularioAcessoCriacao, setFormularioAcessoCriacao] = useState(
    novoFormularioAcessoCriacao,
  );

  const [errosAcessoCriacao, setErrosAcessoCriacao] = useState({});

  // =========================================================
  // VISIBILIDADE DAS SENHAS
  // =========================================================

  const [mostrarNovaSenha, setMostrarNovaSenha] = useState(false);
  const [mostrarConfirmacaoSenha, setMostrarConfirmacaoSenha] = useState(false);

  // =========================================================
  // CARREGAMENTO PRINCIPAL
  // =========================================================

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let ativo = true;

    setFormulario(novoFormulario());
    setNomeDigitado("");
    setCidadesDisponiveis([]);
    setErrosCampos({});
    setAvisosCampos({});
    setErro("");
    setEnderecoBaseOriginal("");
    setEditandoEndereco(!editando);
    setDadosCarregados(!editando);
    setCarregando(true);
    setAbaAtiva(0);

    setAcesso(null);
    setAcessoCarregado(false);
    setCarregandoAcesso(false);
    setErroAcesso("");
    setSucessoAcesso("");
    setFormularioSenha(novoFormularioSenha());
    setErrosSenha({});
    setSalvandoSenha(false);
    setExigirTrocaSenha(false);

    setFormularioAcessoCriacao(novoFormularioAcessoCriacao());
    setErrosAcessoCriacao({});

    setMostrarNovaSenha(false);
    setMostrarConfirmacaoSenha(false);

    const carregar = async () => {
      try {
        const requests = [
          fetch(
            editando
              ? api.listAvailableCitiesForSre(id)
              : api.listAvailableCities(),
            {
              credentials: "include",
            },
          ),
        ];

        if (editando) {
          requests.push(
            fetch(api.getSre(id), {
              credentials: "include",
            }),
          );
        }

        const responses = await Promise.all(requests);

        const dados = await Promise.all(
          responses.map((response) => response.json().catch(() => ({}))),
        );

        const indiceErro = responses.findIndex((response) => !response.ok);

        if (indiceErro !== -1) {
          throw new Error(
            obterMensagemErro(
              dados[indiceErro],
              "Não foi possível carregar os dados da SRE.",
            ),
          );
        }

        if (!ativo) {
          return;
        }

        setCidadesDisponiveis(Array.isArray(dados[0]) ? dados[0] : []);

        // =====================================================
        // MODO EDIÇÃO
        // =====================================================

        if (editando) {
          const sre = dados[1];

          if (!sre?.id) {
            throw new Error("Não foi possível carregar a SRE.");
          }

          const address = String(sre.address || "");
          const endereco = lerEndereco(address);
          const partes = address.split(/\s*\|\s*Site:\s*/i);
          const enderecoBase = String(partes[0] || "").trim();
          const site = String(endereco?.site || partes[1] || "").trim();

          const telefones = separarTelefones(sre.phone).map(
            formatarTelefoneExistente,
          );

          setFormulario({
            ...novoFormulario(),
            name: sre.name || "",
            email: sre.email || "",
            phones: telefones.length ? telefones : [""],
            site,
            cep: formatarCep(endereco?.cep || ""),
            state: endereco?.state || "",
            city: endereco?.city || "",
            neighborhood: endereco?.neighborhood || "",
            street: endereco?.street || "",
            number: endereco?.number || "",
            active: sre.active ?? true,
            cities: Array.isArray(sre.cities) ? sre.cities : [],
          });

          setEnderecoBaseOriginal(enderecoBase);
          setEditandoEndereco(!endereco);
        }

        setDadosCarregados(true);
      } catch (error) {
        if (ativo) {
          setErro(error.message || "Não foi possível carregar os dados.");
        }
      } finally {
        if (ativo) {
          setCarregando(false);
        }
      }
    };

    carregar();

    return () => {
      ativo = false;
    };
  }, [open, editando, id]);

  // =========================================================
  // CARREGAMENTO DO ACESSO — SOMENTE EDIÇÃO
  // =========================================================

  useEffect(() => {
    if (!open || !editando || abaAtiva !== 2 || acessoCarregado) {
      return undefined;
    }

    let ativo = true;

    const carregarAcesso = async () => {
      try {
        setCarregandoAcesso(true);
        setErroAcesso("");
        setSucessoAcesso("");

        if (typeof api.getSreAccess !== "function") {
          throw new Error(
            "A consulta do acesso da SRE ainda não está configurada no frontend.",
          );
        }

        const response = await fetch(api.getSreAccess(id), {
          credentials: "include",
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            obterMensagemErro(
              data,
              "Não foi possível carregar os dados de acesso da SRE.",
            ),
          );
        }

        if (!ativo) {
          return;
        }

        setAcesso(data);

        if (typeof data?.exigirTrocaSenha === "boolean") {
          setExigirTrocaSenha(data.exigirTrocaSenha);
        }
      } catch (error) {
        if (ativo) {
          setErroAcesso(
            error.message ||
              "Não foi possível carregar os dados de acesso da SRE.",
          );
        }
      } finally {
        if (ativo) {
          setCarregandoAcesso(false);
          setAcessoCarregado(true);
        }
      }
    };

    carregarAcesso();

    return () => {
      ativo = false;
    };
  }, [open, editando, abaAtiva, acessoCarregado, id]);

  // =========================================================
  // LIMPEZA
  // =========================================================

  const limpar = () => {
    setFormulario(novoFormulario());
    setNomeDigitado("");

    validacaoDadosSreSeq.current.name += 1;
    validacaoDadosSreSeq.current.email += 1;

    setErrosCampos({});
    setAvisosCampos({});
    setErro("");
    setEnderecoBaseOriginal("");
    setEditandoEndereco(!editando);
    setAbaAtiva(0);

    setAcesso(null);
    setAcessoCarregado(false);
    setCarregandoAcesso(false);
    setErroAcesso("");
    setSucessoAcesso("");
    setFormularioSenha(novoFormularioSenha());
    setErrosSenha({});
    setExigirTrocaSenha(false);

    setFormularioAcessoCriacao(novoFormularioAcessoCriacao());
    setErrosAcessoCriacao({});

    setMostrarNovaSenha(false);
    setMostrarConfirmacaoSenha(false);
  };

  // =========================================================
  // FECHAR
  // =========================================================

  const fechar = () => {
    if (salvando || salvandoSenha) {
      return;
    }

    limpar();

    if (onClose) {
      onClose();
      return;
    }

    navigate("/admin/visualizar/sre");
  };

  // =========================================================
  // CAMPOS DA SRE
  // =========================================================

  const alterarCampo = (event) => {
    const { name, value } = event.target;

    const novoValor =
      name === "cep"
        ? formatarCep(value)
        : name === "state"
          ? value.toUpperCase().slice(0, 2)
          : value;

    setFormulario((atual) => ({
      ...atual,
      [name]: novoValor,
    }));

    if (CAMPOS_ENDERECO.has(name)) {
      setEditandoEndereco(true);
    }

    setErrosCampos((atual) => ({
      ...atual,
      [name]: "",
    }));

    setAvisosCampos((atual) => ({
      ...atual,
      [name]: "",
    }));

    if (name === "name" || name === "email") {
      validacaoDadosSreSeq.current[name] += 1;
    }

    setErro("");
  };

  const validarDisponibilidadeDadoSre = async (campo) => {
    if (editando) {
      return;
    }

    const valor = String(formulario[campo] || "").trim();

    if (!valor) {
      return;
    }

    const sequencia = ++validacaoDadosSreSeq.current[campo];

    try {
      const response = await fetch(
        api.checkSreDataAvailability(
          campo === "name"
            ? {
                nomeSre: valor,
              }
            : {
                emailInstitucional: valor,
              },
        ),
        {
          credentials: "include",
          cache: "no-store",
        },
      );

      const data = await response.json().catch(() => ({}));

      if (validacaoDadosSreSeq.current[campo] !== sequencia) {
        return;
      }

      if (!response.ok) {
        throw new Error(
          obterMensagemErro(
            data,
            "Não foi possível verificar a disponibilidade.",
          ),
        );
      }

      const disponivel =
        campo === "name"
          ? data.nomeSreDisponivel
          : data.emailInstitucionalDisponivel;

      setErrosCampos((atual) => ({
        ...atual,
        [campo]:
          disponivel === false
            ? campo === "name"
              ? "Já existe uma SRE cadastrada com nome equivalente."
              : "Este e-mail institucional já está cadastrado."
            : "",
      }));

      setAvisosCampos((atual) => ({
        ...atual,
        [campo]:
          campo === "name" && disponivel !== false && data.nomeSreSemelhante
            ? `Possível cadastro semelhante encontrado: "${data.nomeSreSemelhante}". Confira antes de continuar.`
            : "",
      }));
    } catch (error) {
      if (validacaoDadosSreSeq.current[campo] !== sequencia) {
        return;
      }

      setErrosCampos((atual) => ({
        ...atual,
        [campo]:
          error.message || "Não foi possível verificar a disponibilidade.",
      }));

      setAvisosCampos((atual) => ({
        ...atual,
        [campo]: "",
      }));
    }
  };

  const alterarTelefone = (indice, valor) => {
    setFormulario((atual) => ({
      ...atual,
      phones: atual.phones.map((telefone, i) =>
        i === indice ? formatarTelefone(valor) : telefone,
      ),
    }));

    setErrosCampos((atual) => ({
      ...atual,
      phones: "",
    }));
  };

  // =========================================================
  // DADOS DE ACESSO — EDIÇÃO
  // =========================================================

  const alterarCampoSenha = (event) => {
    const { name, value } = event.target;

    setFormularioSenha((atual) => ({
      ...atual,
      [name]: value,
    }));

    setErrosSenha((atual) => ({
      ...atual,
      [name]: "",
    }));

    setErroAcesso("");
    setSucessoAcesso("");
  };

  // =========================================================
  // DADOS DE ACESSO — CRIAÇÃO
  // =========================================================

  const alterarCampoAcessoCriacao = (event) => {
    const { name, value } = event.target;

    setFormularioAcessoCriacao((atual) => ({
      ...atual,
      [name]: value,
    }));

    setErrosAcessoCriacao((atual) => ({
      ...atual,
      [name]: "",
    }));
  };

  // =========================================================
  // SALVAR SRE
  // =========================================================

  const salvar = async (event) => {
    event.preventDefault();

    if (salvando || carregando || !dadosCarregados) {
      return;
    }

    const selecionadas = [...formulario.cities];
    const textoPendente = nomeDigitado.trim().replace(/\s+/g, " ");

    if (textoPendente) {
      selecionadas.push(textoPendente);
    }

    const erros = {};
    const errosAcesso = {};

    // DADOS INSTITUCIONAIS

    if (!formulario.name.trim()) {
      erros.name = "Informe o nome da SRE.";
    }

    if (!formulario.email.trim()) {
      erros.email = "Informe o e-mail institucional.";
    }

    if (!formulario.phones.some((telefone) => telefone.trim())) {
      erros.phones = "Informe ao menos um telefone.";
    } else if (
      formulario.phones.some(
        (telefone) =>
          telefone.trim() && !/^\d{10,11}$/.test(telefone.replace(/\D/g, "")),
      )
    ) {
      erros.phones = "Informe DDD e telefone com 10 ou 11 dígitos.";
    }

    // SITE

    const siteNormalizado = normalizarSite(formulario.site);

    if (siteNormalizado) {
      try {
        const url = new URL(siteNormalizado);

        if (!["https:", "http:"].includes(url.protocol)) {
          throw new Error();
        }
      } catch {
        erros.site =
          "Informe um endereço iniciado por https://, http:// ou www.";
      }
    }

    // ENDEREÇO

    if (!editando || editandoEndereco || !enderecoBaseOriginal) {
      if (!/^\d{5}-\d{3}$/.test(formulario.cep)) {
        erros.cep = "Informe o CEP com 8 dígitos.";
      }

      if (!/^[A-Z]{2}$/.test(formulario.state.trim())) {
        erros.state = "Informe a sigla do estado.";
      }

      for (const campoEndereco of [
        "city",
        "neighborhood",
        "street",
        "number",
      ]) {
        if (!formulario[campoEndereco].trim()) {
          erros[campoEndereco] = "Campo obrigatório.";
        }
      }
    }

    // CIDADES

    if (!selecionadas.length) {
      erros.cities = "Selecione ou digite um município.";
    }

    const cityIds = [];
    const cityNames = [];
    const nomesIncluidos = new Set();

    for (const item of selecionadas) {
      if (typeof item === "string") {
        const nome = item.trim().replace(/\s+/g, " ");
        const chave = nome.toLocaleLowerCase("pt-BR");

        if (nome.length < 2) {
          erros.cities = "Informe um nome de município válido.";
        }

        if (nome && !nomesIncluidos.has(chave)) {
          cityNames.push(nome);
          nomesIncluidos.add(chave);
        }
      } else if (item?.id && !cityIds.includes(item.id)) {
        cityIds.push(item.id);
      }
    }

    // CRIAÇÃO — VALIDAÇÃO DOS DADOS DE ACESSO

    if (!editando) {
      const { senhaProvisoria, confirmarSenhaProvisoria } =
        formularioAcessoCriacao;

      if (!senhaProvisoria) {
        errosAcesso.senhaProvisoria = "Informe a senha provisória.";
      } else if (senhaProvisoria.length < 8) {
        errosAcesso.senhaProvisoria =
          "A senha deve possuir no mínimo 8 caracteres.";
      } else if (!/[A-Z]/.test(senhaProvisoria)) {
        errosAcesso.senhaProvisoria = "Informe ao menos uma letra maiúscula.";
      } else if (!/[a-z]/.test(senhaProvisoria)) {
        errosAcesso.senhaProvisoria = "Informe ao menos uma letra minúscula.";
      } else if (!/\d/.test(senhaProvisoria)) {
        errosAcesso.senhaProvisoria = "Informe ao menos um número.";
      } else if (!/[@$!%*?&]/.test(senhaProvisoria)) {
        errosAcesso.senhaProvisoria =
          "Informe ao menos um caractere especial: @ $ ! % * ? &.";
      } else if (!/^[A-Za-z\d@$!%*?&]+$/.test(senhaProvisoria)) {
        errosAcesso.senhaProvisoria =
          "Use apenas letras, números e os caracteres especiais @ $ ! % * ? &.";
      }

      if (!confirmarSenhaProvisoria) {
        errosAcesso.confirmarSenhaProvisoria = "Confirme a senha provisória.";
      } else if (senhaProvisoria !== confirmarSenhaProvisoria) {
        errosAcesso.confirmarSenhaProvisoria =
          "As senhas informadas não coincidem.";
      }
    }

    // ADDRESS

    const site = normalizarSite(formulario.site);
    let address;

    if (!editando || editandoEndereco || !enderecoBaseOriginal) {
      address = montarEndereco(formulario);
    } else {
      address = site
        ? `${enderecoBaseOriginal} | Site: ${site}`
        : enderecoBaseOriginal;
    }

    if (address.length > 255) {
      erros.street =
        "Endereço e site juntos excedem 255 caracteres. Resuma os dados ou use um link mais curto.";
    }

    setErrosCampos(erros);
    setErrosAcessoCriacao(errosAcesso);

    // DIRECIONAR PARA A ABA COM ERRO

    const errosDados = Object.keys(erros).filter((campo) => campo !== "cities");

    if (errosDados.length) {
      setAbaAtiva(0);
      return;
    }

    if (erros.cities) {
      setAbaAtiva(1);
      return;
    }

    if (!editando && Object.keys(errosAcesso).length) {
      setAbaAtiva(2);
      return;
    }

    // REVALIDAÇÃO IMEDIATA ANTES DO CADASTRO

    if (!editando) {
      try {
        const responseDisponibilidade = await fetch(
          api.checkSreDataAvailability({
            nomeSre: formulario.name.trim(),
            emailInstitucional: formulario.email.trim(),
          }),
          {
            credentials: "include",
            cache: "no-store",
          },
        );

        const dadosDisponibilidade = await responseDisponibilidade
          .json()
          .catch(() => ({}));

        if (!responseDisponibilidade.ok) {
          throw new Error(
            obterMensagemErro(
              dadosDisponibilidade,
              "Não foi possível confirmar a disponibilidade dos dados da SRE.",
            ),
          );
        }

        const errosDisponibilidade = {};

        if (dadosDisponibilidade.nomeSreDisponivel === false) {
          errosDisponibilidade.name =
            "Já existe uma SRE cadastrada com nome equivalente.";
        }

        if (dadosDisponibilidade.emailInstitucionalDisponivel === false) {
          errosDisponibilidade.email =
            "Este e-mail institucional já está cadastrado.";
        }

        if (Object.keys(errosDisponibilidade).length) {
          setErrosCampos((atual) => ({
            ...atual,
            ...errosDisponibilidade,
          }));

          setAvisosCampos((atual) => ({
            ...atual,
            name: "",
            email: "",
          }));

          setAbaAtiva(0);
          return;
        }

        setAvisosCampos((atual) => ({
          ...atual,
          name: dadosDisponibilidade.nomeSreSemelhante
            ? `Possível cadastro semelhante encontrado: "${dadosDisponibilidade.nomeSreSemelhante}". Confira antes de continuar.`
            : atual.name || "",
        }));
      } catch (error) {
        setErro(
          error.message ||
            "Não foi possível confirmar a disponibilidade dos dados da SRE.",
        );

        setAbaAtiva(0);
        return;
      }
    }

    // REQUISIÇÃO

    try {
      setSalvando(true);
      setErro("");

      const response = await fetch(
        editando ? api.updateSre(id) : api.createSre(),
        {
          method: editando ? "PUT" : "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: formulario.name.trim(),
            email: formulario.email.trim(),
            phone: formulario.phones
              .map((telefone) => telefone.trim())
              .filter(Boolean)
              .join("\n"),
            address,
            active: formulario.active,
            cityIds,
            cityNames,
            ...(!editando
              ? {
                  senhaProvisoria: formularioAcessoCriacao.senhaProvisoria,
                  confirmarSenhaProvisoria:
                    formularioAcessoCriacao.confirmarSenhaProvisoria,
                }
              : {}),
          }),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          obterMensagemErro(data, "Não foi possível salvar a SRE."),
        );
      }

      limpar();

      if (editando && onSaved) {
        onSaved(data);
        return;
      }

      if (!editando && onCreated) {
        onCreated(data);
        return;
      }

      navigate("/admin/visualizar/sre");
    } catch (error) {
      setErro(error.message || "Não foi possível salvar a SRE.");
    } finally {
      setSalvando(false);
    }
  };

  // =========================================================
  // EDIÇÃO — SALVAR DADOS DE ACESSO
  // =========================================================

  const salvarSenha = async (event) => {
    event.preventDefault();

    if (!editando || salvandoSenha || carregandoAcesso) {
      return;
    }

    const novaSenha = formularioSenha.novaSenha;
    const confirmarNovaSenha = formularioSenha.confirmarNovaSenha;
    const erros = {};

    if (!novaSenha) {
      erros.novaSenha = "Informe a nova senha.";
    } else if (novaSenha.length < 8) {
      erros.novaSenha = "A senha deve possuir no mínimo 8 caracteres.";
    } else if (!/[A-Z]/.test(novaSenha)) {
      erros.novaSenha = "A senha deve possuir ao menos uma letra maiúscula.";
    } else if (!/[a-z]/.test(novaSenha)) {
      erros.novaSenha = "A senha deve possuir ao menos uma letra minúscula.";
    } else if (!/\d/.test(novaSenha)) {
      erros.novaSenha = "A senha deve possuir ao menos um número.";
    } else if (!/[^A-Za-z0-9]/.test(novaSenha)) {
      erros.novaSenha = "A senha deve possuir ao menos um caractere especial.";
    }

    if (!confirmarNovaSenha) {
      erros.confirmarNovaSenha = "Confirme a nova senha.";
    } else if (novaSenha !== confirmarNovaSenha) {
      erros.confirmarNovaSenha = "As senhas informadas não coincidem.";
    }

    setErrosSenha(erros);
    setErroAcesso("");
    setSucessoAcesso("");

    if (Object.keys(erros).length) {
      return;
    }

    try {
      setSalvandoSenha(true);

      if (typeof api.updateSreAccessPassword !== "function") {
        throw new Error(
          "A alteração de senha da SRE ainda não está configurada no frontend.",
        );
      }

      const response = await fetch(api.updateSreAccessPassword(id), {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          novaSenha,
          confirmarNovaSenha,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          obterMensagemErro(data, "Não foi possível atualizar a senha da SRE."),
        );
      }

      setFormularioSenha(novoFormularioSenha());
      setErrosSenha({});
      setMostrarNovaSenha(false);
      setMostrarConfirmacaoSenha(false);

      setSucessoAcesso(data?.message || "Senha atualizada com sucesso.");
    } catch (error) {
      setErroAcesso(
        error.message || "Não foi possível atualizar a senha da SRE.",
      );
    } finally {
      setSalvandoSenha(false);
    }
  };

  // =========================================================
  // CAMPO VISUAL PADRÃO
  // =========================================================

  const campoEdicao = ({
    name,
    label,
    placeholder,
    icon,
    required = true,
    type = "text",
    inputProps,
    helperText,
    onBlur,
  }) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        component="label"
        htmlFor={`sre-${name}`}
        sx={{
          display: "block",
          mb: 0.45,
          color: "var(--sear-texto-secundario, #42566f)",
          fontSize: tamanhosModaisGlobais.rotulo,
          fontWeight: 700,
          lineHeight: 1.2,
        }}
      >
        {label}

        {required && (
          <Box
            component="span"
            sx={{
              color: "var(--sear-erro-texto, #d32f2f)",
              ml: 0.35,
            }}
          >
            *
          </Box>
        )}
      </Typography>

      <TextField
        id={`sre-${name}`}
        fullWidth
        size="small"
        type={type}
        name={name}
        placeholder={placeholder}
        value={formulario[name]}
        onChange={alterarCampo}
        onBlur={onBlur}
        error={!!errosCampos[name]}
        helperText={errosCampos[name] || avisosCampos[name] || helperText}
        inputProps={inputProps}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">{icon}</InputAdornment>
          ),
        }}
        sx={{
          ...ESTILO_CAMPO_EDICAO,
          ...(avisosCampos[name] && !errosCampos[name]
            ? {
                "& .MuiFormHelperText-root": {
                  fontSize: tamanhosModaisGlobais.textoAuxiliar,
                  color: "var(--sear-aviso-texto, #b26a00)",
                  fontWeight: 500,
                },
              }
            : {}),
        }}
      />
    </Box>
  );

  // =========================================================
  // ABA 1 — DADOS DA SRE
  // A mesma estrutura atende criação e edição.
  // =========================================================

  const renderDadosSre = () => (
    <Box
      sx={{
        border: "1px solid var(--sear-borda, #d7e7f7)",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "var(--sear-superficie, #ffffff)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: {
            xs: 1.75,
            sm: 2,
          },
          py: 1.45,
          borderBottom: "1px solid var(--sear-borda, #e4edf6)",
          background: "linear-gradient(90deg, var(--sear-superficie-azul, #eef7ff) 0%, var(--sear-superficie-azul, #f8fbff) 100%)",
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            color: "var(--sear-azul, #087cf0)",
            backgroundColor: "var(--sear-superficie-azul, #e1f1ff)",
            border: "1px solid var(--sear-borda, #d1e8fc)",
          }}
        >
          <BuildingsFillIcon sx={{ fontSize: 25 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{
              color: "var(--sear-azul, #0767d8)",
              fontSize: tamanhosModaisGlobais.tituloSecao,
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            Dados da SRE
          </Typography>

          <Typography
            sx={{
              mt: 0.25,
              color: "var(--sear-texto-secundario, #6b7f95)",
              fontSize: tamanhosModaisGlobais.descricao,
              lineHeight: 1.35,
            }}
          >
            Informações institucionais da superintendência.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: {
            xs: 1.75,
            sm: 2,
          },
          py: 1.75,
          display: "grid",
          gap: 1.45,
        }}
      >
        {campoEdicao({
          name: "name",
          label: "Nome da SRE",
          placeholder: "Ex.: SRE Uberlândia",
          onBlur: () => validarDisponibilidadeDadoSre("name"),
          icon: <BuildingsFillIcon sx={{ fontSize: 18 }} />,
        })}

        {campoEdicao({
          name: "email",
          label: "E-mail institucional",
          placeholder: "Ex.: sre@educacao.mg.gov.br",
          type: "email",
          onBlur: () => validarDisponibilidadeDadoSre("email"),
          icon: <EmailRounded sx={{ fontSize: 18 }} />,
        })}

        <Box sx={{ minWidth: 0 }}>
          {formulario.phones.map((telefone, indice) => (
            <Box
              key={indice}
              sx={{
                mb: indice === formulario.phones.length - 1 ? 0 : 1,
              }}
            >
              <Typography
                component="label"
                sx={{
                  display: "block",
                  mb: 0.45,
                  color: "var(--sear-texto-secundario, #42566f)",
                  fontSize: tamanhosModaisGlobais.rotulo,
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                {indice === 0 ? "Telefone" : `Telefone ${indice + 1}`}

                {indice === 0 && (
                  <Box
                    component="span"
                    sx={{
                      color: "var(--sear-erro-texto, #d32f2f)",
                      ml: 0.35,
                    }}
                  >
                    *
                  </Box>
                )}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 0.5,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  type="tel"
                  placeholder="(DD) 0000-0000"
                  value={telefone}
                  onChange={(event) =>
                    alterarTelefone(indice, event.target.value)
                  }
                  error={indice === 0 && !!errosCampos.phones}
                  helperText={indice === 0 ? errosCampos.phones : ""}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneRounded sx={{ fontSize: 18 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={ESTILO_CAMPO_EDICAO}
                />

                {indice > 0 && (
                  <IconButton
                    type="button"
                    aria-label={`Remover telefone ${indice + 1}`}
                    onClick={() =>
                      setFormulario((atual) => ({
                        ...atual,
                        phones: atual.phones.filter((_, i) => i !== indice),
                      }))
                    }
                    sx={{
                      ...botaoAcaoExcluir,
                      mt: 0.2,
                    }}
                  >
                    <RemoveCircleOutline fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}

          <Button
            type="button"
            size="small"
            startIcon={<Add />}
            onClick={() =>
              setFormulario((atual) => ({
                ...atual,
                phones: [...atual.phones, ""],
              }))
            }
            variant="outlined"
            sx={{
              ...botaoAdicionar,
              fontSize: tamanhosModaisGlobais.botao,
              mt: 0.55,
            }}
          >
            Adicionar telefone
          </Button>
        </Box>

        {campoEdicao({
          name: "site",
          label: "Site",
          placeholder: "https://sre.uberlandia.educacao.mg.gov.br",
          type: "text",
          required: false,
          inputProps: {
            inputMode: "url",
          },
          helperText:
            "Aceita endereços iniciados por https://, http:// ou www. Endereços com www. serão salvos como https://.",
          onBlur: (event) => {
            const site = normalizarSite(event.target.value);

            setFormulario((atual) => ({
              ...atual,
              site,
            }));
          },
          icon: <LinkRounded sx={{ fontSize: 18 }} />,
        })}

        <Box
          sx={{
            pt: 0.3,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            sx={{
              height: 1,
              flex: 1,
              backgroundColor: "var(--sear-superficie-azul, #e5edf5)",
            }}
          />

          <Typography
            sx={{
              color: "var(--sear-texto-secundario, #6d8198)",
              fontSize: tamanhosModaisGlobais.rotulo,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Endereço
          </Typography>

          <Box
            sx={{
              height: 1,
              flex: 1,
              backgroundColor: "var(--sear-superficie-azul, #e5edf5)",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(12, minmax(0, 1fr))",
            },
            columnGap: 1.45,
            rowGap: 1.45,
            alignItems: "start",
          }}
        >
          <Box
            sx={{
              gridColumn: {
                xs: "1",
                sm: "span 9",
              },
              minWidth: 0,
            }}
          >
            {campoEdicao({
              name: "cep",
              label: "CEP",
              placeholder: "00000-000",
              icon: <LocationOnRounded sx={{ fontSize: 18 }} />,
              inputProps: {
                inputMode: "numeric",
                maxLength: 9,
              },
            })}
          </Box>

          <Box
            sx={{
              gridColumn: {
                xs: "1",
                sm: "span 3",
              },
              minWidth: 0,
            }}
          >
            {campoEdicao({
              name: "state",
              label: "Estado",
              placeholder: "MG",
              icon: <MapRounded sx={{ fontSize: 18 }} />,
              inputProps: {
                maxLength: 2,
              },
            })}
          </Box>

          <Box
            sx={{
              gridColumn: {
                xs: "1",
                sm: "span 6",
              },
              minWidth: 0,
            }}
          >
            {campoEdicao({
              name: "city",
              label: "Município",
              placeholder: "Ex.: Uberlândia",
              icon: <LocationCityRounded sx={{ fontSize: 18 }} />,
            })}
          </Box>

          <Box
            sx={{
              gridColumn: {
                xs: "1",
                sm: "span 6",
              },
              minWidth: 0,
            }}
          >
            {campoEdicao({
              name: "neighborhood",
              label: "Bairro",
              placeholder: "Ex.: Tabajaras",
              icon: <HomeWorkRounded sx={{ fontSize: 18 }} />,
            })}
          </Box>

          <Box
            sx={{
              gridColumn: {
                xs: "1",
                sm: "span 9",
              },
              minWidth: 0,
            }}
          >
            {campoEdicao({
              name: "street",
              label: "Rua",
              placeholder: "Ex.: Rua Coronel Severiano",
              icon: <SignpostRounded sx={{ fontSize: 18 }} />,
            })}
          </Box>

          <Box
            sx={{
              gridColumn: {
                xs: "1",
                sm: "span 3",
              },
              minWidth: 0,
            }}
          >
            {campoEdicao({
              name: "number",
              label: "Número",
              placeholder: "Ex.: 351-A",
              icon: <NumbersRounded sx={{ fontSize: 18 }} />,
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // =========================================================
  // ABA 2 — CIDADES
  // =========================================================

  const obterNomeCidade = (cidade) =>
    typeof cidade === "string"
      ? cidade.trim()
      : String(cidade?.name || "").trim();

  const obterChaveCidade = (cidade) =>
    obterNomeCidade(cidade).toLocaleLowerCase("pt-BR");

  const removerCidade = (indice) => {
    setFormulario((atual) => ({
      ...atual,
      cities: atual.cities.filter((_, i) => i !== indice),
    }));

    setErrosCampos((atual) => ({
      ...atual,
      cities: "",
    }));
  };

  const adicionarCidade = (cidade) => {
    if (!cidade) {
      return;
    }

    const nome = obterNomeCidade(cidade);

    if (!nome) {
      return;
    }

    const chave = obterChaveCidade(cidade);

    setFormulario((atual) => {
      const jaExiste = atual.cities.some((item) => {
        if (
          typeof cidade !== "string" &&
          typeof item !== "string" &&
          cidade?.id &&
          item?.id
        ) {
          return cidade.id === item.id;
        }

        return obterChaveCidade(item) === chave;
      });

      if (jaExiste) {
        return atual;
      }

      return {
        ...atual,
        cities: [...atual.cities, cidade],
      };
    });

    setNomeDigitado("");

    setErrosCampos((atual) => ({
      ...atual,
      cities: "",
    }));
  };

  const cidadesDisponiveisParaAdicionar = cidadesDisponiveis.filter(
    (cidade) => {
      const chave = obterChaveCidade(cidade);

      return !formulario.cities.some((item) => {
        if (typeof item !== "string" && cidade?.id && item?.id) {
          return cidade.id === item.id;
        }

        return obterChaveCidade(item) === chave;
      });
    },
  );

  const renderCidades = () => (
    <Box
      sx={{
        border: "1px solid var(--sear-borda, #d7e7f7)",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "var(--sear-superficie, #ffffff)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: {
            xs: 1.75,
            sm: 2,
          },
          py: 1.45,
          borderBottom: "1px solid var(--sear-borda, #e4edf6)",
          background: "linear-gradient(90deg, var(--sear-superficie-azul, #eef7ff) 0%, var(--sear-superficie-azul, #f8fbff) 100%)",
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            color: "var(--sear-azul, #087cf0)",
            backgroundColor: "var(--sear-superficie-azul, #e1f1ff)",
            border: "1px solid var(--sear-borda, #d1e8fc)",
          }}
        >
          <LocationCityRounded sx={{ fontSize: 25 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{
              color: "var(--sear-azul, #0767d8)",
              fontSize: tamanhosModaisGlobais.tituloSecao,
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            Cidades vinculadas
          </Typography>

          <Typography
            sx={{
              mt: 0.25,
              color: "var(--sear-texto-secundario, #6b7f95)",
              fontSize: tamanhosModaisGlobais.descricao,
              lineHeight: 1.35,
            }}
          >
            Municípios atendidos por esta Superintendência Regional de Ensino.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          px: {
            xs: 1.75,
            sm: 2,
          },
          py: 1.75,
        }}
      >
        <Typography
          sx={{
            color: "var(--sear-texto-secundario, #42566f)",
            fontSize: tamanhosModaisGlobais.rotulo,
            fontWeight: 700,
            mb: 0.9,
          }}
        >
          Municípios atualmente vinculados
        </Typography>

        {formulario.cities.length ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.8,
              minHeight: 34,
            }}
          >
            {formulario.cities.map((cidade, indice) => (
              <Chip
                key={
                  typeof cidade !== "string" && cidade?.id
                    ? `cidade-${cidade.id}`
                    : `cidade-${obterChaveCidade(cidade)}-${indice}`
                }
                label={obterNomeCidade(cidade)}
                onDelete={() => removerCidade(indice)}
                disabled={salvando}
                sx={{
                  height: 30,
                  border: "1px solid var(--sear-borda, #c5def8)",
                  borderRadius: 1,
                  backgroundColor: "var(--sear-superficie-azul, #dcecff)",
                  color: "var(--sear-azul, #0767d8)",
                  fontSize: tamanhosModaisGlobais.campo,
                  fontWeight: 600,

                  "& .MuiChip-label": {
                    px: 1.15,
                  },

                  "& .MuiChip-deleteIcon": {
                    color: "var(--sear-azul, #087cf0)",
                    fontSize: 17,

                    "&:hover": {
                      color: "var(--sear-azul, #0759bf)",
                    },
                  },
                }}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              px: 1.4,
              py: 1.25,
              borderRadius: 1.25,
              border: "1px dashed var(--sear-borda, #cbdbea)",
              backgroundColor: "var(--sear-superficie, #fafcff)",
            }}
          >
            <Typography
              sx={{
                color: "var(--sear-texto-secundario, #71869d)",
                fontSize: tamanhosModaisGlobais.textoAuxiliar,
              }}
            >
              Nenhum município vinculado no momento.
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            my: 1.7,
            height: 1,
            backgroundColor: "var(--sear-superficie-azul, #e5edf5)",
          }}
        />

        <Typography
          sx={{
            color: "var(--sear-texto-secundario, #42566f)",
            fontSize: tamanhosModaisGlobais.rotulo,
            fontWeight: 700,
            mb: 0.65,
          }}
        >
          Adicionar município
        </Typography>

        <Autocomplete
          freeSolo
          clearOnBlur={false}
          selectOnFocus
          handleHomeEndKeys
          size="small"
          ListboxProps={{
            sx: {
              "& .MuiAutocomplete-option": {
                fontSize: tamanhosModaisGlobais.opcaoSelecao,
              },
            },
          }}
          options={cidadesDisponiveisParaAdicionar}
          value={null}
          inputValue={nomeDigitado}
          loading={carregando}
          disabled={salvando}
          getOptionLabel={(item) =>
            typeof item === "string" ? item : item.name || ""
          }
          isOptionEqualToValue={(opcao, valor) =>
            typeof valor !== "string" && opcao.id === valor?.id
          }
          onInputChange={(_, valor, motivo) => {
            if (motivo !== "reset") {
              setNomeDigitado(valor);
            }

            setErrosCampos((atual) => ({
              ...atual,
              cities: "",
            }));
          }}
          onChange={(_, cidade) => {
            adicionarCidade(cidade);
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              minHeight: 42,
              borderRadius: 1.25,
              backgroundColor: "var(--sear-superficie, #ffffff)",
              fontSize: tamanhosModaisGlobais.campo,
            },

            "& .MuiFormHelperText-root": {
              fontSize: tamanhosModaisGlobais.textoAuxiliar,
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Selecione ou digite um município..."
              error={!!errosCampos.cities}
              helperText={
                errosCampos.cities ||
                "Selecione um município disponível ou digite um novo nome."
              }
              InputProps={{
                ...params.InputProps,

                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <Add
                        sx={{
                          color: "var(--sear-azul, #087cf0)",
                          fontSize: 19,
                        }}
                      />
                    </InputAdornment>

                    {params.InputProps.startAdornment}
                  </>
                ),

                endAdornment: (
                  <>
                    {carregando && <CircularProgress size={18} />}

                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Box
          sx={{
            mt: 1.35,
            display: "flex",
            alignItems: "flex-start",
            gap: 0.8,
          }}
        >
          <InfoRounded
            sx={{
              mt: 0.05,
              color: "var(--sear-azul, #087cf0)",
              fontSize: 17,
              flexShrink: 0,
            }}
          />

          <Typography
            sx={{
              color: "var(--sear-texto-secundario, #687f98)",
              fontSize: tamanhosModaisGlobais.textoAuxiliar,
              lineHeight: 1.45,
            }}
          >
            Cada município pode pertencer a apenas uma SRE. A alteração só é
            efetivada ao salvar.
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  // =========================================================
  // REQUISITOS DA SENHA
  // =========================================================

  const montarRequisitosSenha = (senha) => [
    {
      texto: "Mínimo de 8 caracteres",
      valido: senha.length >= 8,
    },
    {
      texto: "1 letra maiúscula",
      valido: /[A-Z]/.test(senha),
    },
    {
      texto: "1 letra minúscula",
      valido: /[a-z]/.test(senha),
    },
    {
      texto: "1 número",
      valido: /\d/.test(senha),
    },
    {
      texto: "1 caractere especial (@ $ ! % * ? &)",
      valido: /[@$!%*?&]/.test(senha),
    },
  ];

  // =========================================================
  // PAINEL DE REQUISITOS
  // =========================================================

  const renderRequisitosSenha = (senha) => {
    const requisitos = montarRequisitosSenha(senha);

    return (
      <Box
        sx={{
          p: 1.35,
          border: "1px solid var(--sear-borda, #d6eafb)",
          borderRadius: 1.25,
          backgroundColor: "var(--sear-superficie-azul, #eaf6ff)",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.6,
            mb: 1,
          }}
        >
          <ShieldRounded
            sx={{
              color: "var(--sear-azul, #087cf0)",
              fontSize: 18,
            }}
          />

          <Typography
            sx={{
              color: "var(--sear-azul, #0767d8)",
              fontSize: tamanhosModaisGlobais.rotulo,
              fontWeight: 700,
            }}
          >
            Requisitos da senha
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 0.65,
          }}
        >
          {requisitos.map((requisito) => (
            <Box
              key={requisito.texto}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.65,
              }}
            >
              <CheckCircleRounded
                sx={{
                  fontSize: 15,
                  color: requisito.valido ? "var(--sear-sucesso-texto, #51a86d)" : "var(--sear-texto-suave, #aebdca)",
                }}
              />

              <Typography
                sx={{
                  color: requisito.valido ? "var(--sear-sucesso-texto, #4b6f5a)" : "var(--sear-texto-secundario, #6f8297)",
                  fontSize: tamanhosModaisGlobais.textoAuxiliar,
                  fontWeight: requisito.valido ? 600 : 400,
                  lineHeight: 1.3,
                }}
              >
                {requisito.texto}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  // =========================================================
  // CAMPO DE SENHA COM VISIBILIDADE
  // =========================================================

  const renderCampoSenha = ({
    label,
    name,
    value,
    error,
    helperText,
    onChange,
    mostrar,
    setMostrar,
  }) => (
    <Box>
      <Typography
        sx={{
          color: "var(--sear-texto-secundario, #42566f)",
          fontSize: tamanhosModaisGlobais.rotulo,
          fontWeight: 700,
          mb: 0.45,
        }}
      >
        {label}
      </Typography>

      <TextField
        fullWidth
        size="small"
        type={mostrar ? "text" : "password"}
        name={name}
        autoComplete="new-password"
        value={value}
        onChange={onChange}
        error={!!error}
        helperText={helperText}
        sx={ESTILO_CAMPO_EDICAO}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                type="button"
                size="small"
                edge="end"
                onClick={() => setMostrar((atual) => !atual)}
              >
                {mostrar ? (
                  <VisibilityOffRounded sx={{ fontSize: 18 }} />
                ) : (
                  <VisibilityRounded sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );

  // =========================================================
  // ABA 3 — CRIAÇÃO
  // =========================================================

  const renderDadosAcessoCriacao = () => {
    const senha = formularioAcessoCriacao.senhaProvisoria;

    return (
      <Box
        sx={{
          border: "1px solid var(--sear-borda, #d7e7f7)",
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "var(--sear-superficie, #ffffff)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: {
              xs: 1.75,
              sm: 2,
            },
            py: 1.45,
            borderBottom: "1px solid var(--sear-borda, #e4edf6)",
            background: "linear-gradient(90deg, var(--sear-superficie-azul, #eef7ff) 0%, var(--sear-superficie-azul, #f8fbff) 100%)",
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              color: "var(--sear-azul, #087cf0)",
              backgroundColor: "var(--sear-superficie-azul, #e1f1ff)",
              border: "1px solid var(--sear-borda, #d1e8fc)",
            }}
          >
            <LockResetRounded sx={{ fontSize: 25 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h3"
              sx={{
                color: "var(--sear-azul, #0767d8)",
                fontSize: tamanhosModaisGlobais.tituloSecao,
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              Dados de Acesso
            </Typography>

            <Typography
              sx={{
                mt: 0.25,
                color: "var(--sear-texto-secundario, #6b7f95)",
                fontSize: tamanhosModaisGlobais.descricao,
                lineHeight: 1.35,
              }}
            >
              O nome e o e-mail de acesso são vinculados aos dados
              institucionais da SRE. Defina apenas a senha provisória.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            px: {
              xs: 1.75,
              sm: 2,
            },
            py: 1.75,
            display: "grid",
            gap: 1.45,
          }}
        >
          <Box>
            <Typography
              sx={{
                color: "var(--sear-texto-secundario, #42566f)",
                fontSize: tamanhosModaisGlobais.rotulo,
                fontWeight: 700,
                mb: 0.45,
              }}
            >
              Nome do usuário
            </Typography>

            <TextField
              fullWidth
              size="small"
              value={formulario.name}
              helperText="Preenchido automaticamente com o Nome da SRE."
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <BuildingsFillIcon
                      sx={{
                        color: "var(--sear-azul, #087cf0)",
                        fontSize: 18,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={ESTILO_CAMPO_EDICAO}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                color: "var(--sear-texto-secundario, #42566f)",
                fontSize: tamanhosModaisGlobais.rotulo,
                fontWeight: 700,
                mb: 0.45,
              }}
            >
              E-mail de acesso
            </Typography>

            <TextField
              fullWidth
              size="small"
              type="email"
              value={formulario.email}
              helperText="Preenchido automaticamente com o E-mail institucional e utilizado para login."
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailRounded
                      sx={{
                        color: "var(--sear-azul, #087cf0)",
                        fontSize: 18,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={ESTILO_CAMPO_EDICAO}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minHeight: 38,
              gap: 0.5,
            }}
          >
            <Switch
              checked
              disabled
              size="small"
              sx={{
                ml: -0.5,

                "& .MuiSwitch-switchBase.Mui-disabled": {
                  color: "var(--sear-azul, #087cf0)",
                },

                "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                  backgroundColor: "var(--sear-acao-azul, #087cf0)",
                  opacity: 0.5,
                },
              }}
            />

            <Typography
              sx={{
                color: "var(--sear-texto-secundario, #42566f)",
                fontSize: tamanhosModaisGlobais.rotulo,
                fontWeight: 600,
              }}
            >
              Exigir troca de senha no primeiro acesso
            </Typography>

            <Tooltip
              title="A senha cadastrada é provisória. O usuário deverá substituí-la no primeiro acesso."
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: tamanhosModaisGlobais.textoAuxiliar,
                  },
                },
              }}
            >
              <HelpOutlineRounded
                sx={{
                  ml: 0.25,
                  color: "var(--sear-texto-suave, #7b91a8)",
                  fontSize: 16,
                  cursor: "help",
                }}
              />
            </Tooltip>
          </Box>

          <Box
            sx={{
              mt: 0.2,
              p: {
                xs: 1.4,
                sm: 1.6,
              },
              border: "1px solid var(--sear-borda, #d7e7f7)",
              borderRadius: 1.5,
              backgroundColor: "var(--sear-superficie-azul, #f8fbff)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.65,
                mb: 1.4,
              }}
            >
              <KeyRounded
                sx={{
                  color: "var(--sear-azul, #087cf0)",
                  fontSize: 18,
                }}
              />

              <Typography
                sx={{
                  color: "var(--sear-azul, #0767d8)",
                  fontSize: tamanhosModaisGlobais.rotulo,
                  fontWeight: 700,
                }}
              >
                Senha provisória de acesso
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1.55fr) minmax(210px, 0.8fr)",
                },
                gap: 1.6,
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gap: 1.25,
                  minWidth: 0,
                }}
              >
                {renderCampoSenha({
                  label: "Senha provisória",
                  name: "senhaProvisoria",
                  value: formularioAcessoCriacao.senhaProvisoria,
                  error: errosAcessoCriacao.senhaProvisoria,
                  helperText: errosAcessoCriacao.senhaProvisoria,
                  onChange: alterarCampoAcessoCriacao,
                  mostrar: mostrarNovaSenha,
                  setMostrar: setMostrarNovaSenha,
                })}

                {renderCampoSenha({
                  label: "Confirmar senha provisória",
                  name: "confirmarSenhaProvisoria",
                  value: formularioAcessoCriacao.confirmarSenhaProvisoria,
                  error: errosAcessoCriacao.confirmarSenhaProvisoria,
                  helperText: errosAcessoCriacao.confirmarSenhaProvisoria,
                  onChange: alterarCampoAcessoCriacao,
                  mostrar: mostrarConfirmacaoSenha,
                  setMostrar: setMostrarConfirmacaoSenha,
                })}
              </Box>

              {renderRequisitosSenha(senha)}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.8,
              px: 1.25,
              py: 1,
              border: "1px solid var(--sear-borda, #d9ebfa)",
              borderRadius: 1.25,
              backgroundColor: "var(--sear-superficie-azul, #edf8ff)",
            }}
          >
            <InfoRounded
              sx={{
                mt: 0.05,
                color: "var(--sear-azul, #087cf0)",
                fontSize: 17,
                flexShrink: 0,
              }}
            />

            <Typography
              sx={{
                color: "var(--sear-texto-secundario, #687f98)",
                fontSize: tamanhosModaisGlobais.textoAuxiliar,
                lineHeight: 1.45,
              }}
            >
              O e-mail de acesso será mantido após o primeiro login. Apenas a
              senha cadastrada é provisória e deverá ser alterada pelo usuário
              no primeiro acesso.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // =========================================================
  // ABA 3 — EDIÇÃO
  // =========================================================

  const renderDadosAcessoEdicao = () => {
    if (carregandoAcesso) {
      return (
        <Box
          sx={{
            minHeight: 260,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    const senha = formularioSenha.novaSenha;

    return (
      <Box
        sx={{
          border: "1px solid var(--sear-borda, #d7e7f7)",
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "var(--sear-superficie, #ffffff)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: {
              xs: 1.75,
              sm: 2,
            },
            py: 1.45,
            borderBottom: "1px solid var(--sear-borda, #e4edf6)",
            background: "linear-gradient(90deg, var(--sear-superficie-azul, #eef7ff) 0%, var(--sear-superficie-azul, #f8fbff) 100%)",
          }}
        >
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              color: "var(--sear-azul, #087cf0)",
              backgroundColor: "var(--sear-superficie-azul, #e1f1ff)",
              border: "1px solid var(--sear-borda, #d1e8fc)",
            }}
          >
            <LockResetRounded sx={{ fontSize: 25 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h3"
              sx={{
                color: "var(--sear-azul, #0767d8)",
                fontSize: tamanhosModaisGlobais.tituloSecao,
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              Dados de Acesso
            </Typography>

            <Typography
              sx={{
                mt: 0.25,
                color: "var(--sear-texto-secundario, #6b7f95)",
                fontSize: tamanhosModaisGlobais.descricao,
                lineHeight: 1.35,
              }}
            >
              Nome e e-mail acompanham os dados institucionais. Nesta aba,
              gerencie a redefinição de senha da SRE.
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            px: {
              xs: 1.75,
              sm: 2,
            },
            py: 1.75,
            display: "grid",
            gap: 1.45,
          }}
        >
          <FeedbackInline
            tipo="error"
            mensagem={erroAcesso}
            sx={{
              "& .MuiTypography-root": {
                fontSize: tamanhosModaisGlobais.textoAuxiliar,
              },
            }}
          />

          <FeedbackInline
            tipo="success"
            mensagem={sucessoAcesso}
            sx={{
              "& .MuiTypography-root": {
                fontSize: tamanhosModaisGlobais.textoAuxiliar,
              },
            }}
          />

          <Box>
            <Typography
              sx={{
                color: "var(--sear-texto-secundario, #42566f)",
                fontSize: tamanhosModaisGlobais.rotulo,
                fontWeight: 700,
                mb: 0.45,
              }}
            >
              Nome do usuário
            </Typography>

            <TextField
              fullWidth
              size="small"
              value={formulario.name || acesso?.sreNome || ""}
              helperText="O nome de acesso acompanha o Nome da SRE. Para alterá-lo, edite os Dados da SRE."
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <BuildingsFillIcon
                      sx={{
                        color: "var(--sear-azul, #087cf0)",
                        fontSize: 18,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={ESTILO_CAMPO_EDICAO}
            />
          </Box>

          <Box>
            <Typography
              sx={{
                color: "var(--sear-texto-secundario, #42566f)",
                fontSize: tamanhosModaisGlobais.rotulo,
                fontWeight: 700,
                mb: 0.45,
              }}
            >
              E-mail de acesso
            </Typography>

            <TextField
              fullWidth
              size="small"
              value={formulario.email || acesso?.email || ""}
              helperText="O e-mail de acesso acompanha o E-mail institucional. Para alterá-lo, edite os Dados da SRE."
              InputProps={{
                readOnly: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailRounded
                      sx={{
                        color: "var(--sear-azul, #087cf0)",
                        fontSize: 18,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={ESTILO_CAMPO_EDICAO}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minHeight: 38,
              gap: 0.5,
            }}
          >
            <Switch
              checked={exigirTrocaSenha}
              onChange={(event) => setExigirTrocaSenha(event.target.checked)}
              size="small"
              sx={{
                ml: -0.5,

                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "var(--sear-azul, #087cf0)",
                },

                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "var(--sear-acao-azul, #087cf0)",
                },
              }}
            />

            <Typography
              sx={{
                color: "var(--sear-texto-secundario, #42566f)",
                fontSize: tamanhosModaisGlobais.rotulo,
                fontWeight: 600,
              }}
            >
              Exigir troca de senha no próximo acesso
            </Typography>

            <Tooltip
              title="Ao acessar o sistema, o usuário deverá definir uma nova senha."
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: tamanhosModaisGlobais.textoAuxiliar,
                  },
                },
              }}
            >
              <HelpOutlineRounded
                sx={{
                  ml: 0.25,
                  color: "var(--sear-texto-suave, #7b91a8)",
                  fontSize: 16,
                  cursor: "help",
                }}
              />
            </Tooltip>
          </Box>

          <Box
            sx={{
              mt: 0.2,
              p: {
                xs: 1.4,
                sm: 1.6,
              },
              border: "1px solid var(--sear-borda, #d7e7f7)",
              borderRadius: 1.5,
              backgroundColor: "var(--sear-superficie-azul, #f8fbff)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.65,
                mb: 1.4,
              }}
            >
              <KeyRounded
                sx={{
                  color: "var(--sear-azul, #087cf0)",
                  fontSize: 18,
                }}
              />

              <Typography
                sx={{
                  color: "var(--sear-azul, #0767d8)",
                  fontSize: tamanhosModaisGlobais.rotulo,
                  fontWeight: 700,
                }}
              >
                Redefinir senha de acesso
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1.55fr) minmax(210px, 0.8fr)",
                },
                gap: 1.6,
                alignItems: "stretch",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gap: 1.25,
                  minWidth: 0,
                }}
              >
                {renderCampoSenha({
                  label: "Nova senha temporária",
                  name: "novaSenha",
                  value: formularioSenha.novaSenha,
                  error: errosSenha.novaSenha,
                  helperText: errosSenha.novaSenha,
                  onChange: alterarCampoSenha,
                  mostrar: mostrarNovaSenha,
                  setMostrar: setMostrarNovaSenha,
                })}

                {renderCampoSenha({
                  label: "Confirmar nova senha",
                  name: "confirmarNovaSenha",
                  value: formularioSenha.confirmarNovaSenha,
                  error: errosSenha.confirmarNovaSenha,
                  helperText: errosSenha.confirmarNovaSenha,
                  onChange: alterarCampoSenha,
                  mostrar: mostrarConfirmacaoSenha,
                  setMostrar: setMostrarConfirmacaoSenha,
                })}
              </Box>

              {renderRequisitosSenha(senha)}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.8,
              px: 1.25,
              py: 1,
              border: "1px solid var(--sear-borda, #d9ebfa)",
              borderRadius: 1.25,
              backgroundColor: "var(--sear-superficie-azul, #edf8ff)",
            }}
          >
            <InfoRounded
              sx={{
                mt: 0.05,
                color: "var(--sear-azul, #087cf0)",
                fontSize: 17,
                flexShrink: 0,
              }}
            />

            <Typography
              sx={{
                color: "var(--sear-texto-secundario, #687f98)",
                fontSize: tamanhosModaisGlobais.textoAuxiliar,
                lineHeight: 1.45,
              }}
            >
              O administrador pode redefinir a senha da SRE diretamente nesta
              edição.
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // =========================================================
  // RENDERIZAÇÃO
  // =========================================================

  const abaAcessoEdicao = editando && abaAtiva === 2;

  return (
    <Dialog
      open={open}
      onClose={fechar}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          width: {
            xs: "calc(100% - 24px)",
            sm: 760,
          },
          maxWidth: "760px !important",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 2.5,
          border: "1px solid var(--sear-borda, #d7e7f7)",
          borderTop: "5px solid var(--sear-borda-forte, #087cf0)",
          boxShadow: "0 18px 48px rgba(20, 61, 106, 0.18)",
        },
      }}
    >
      <DialogTitle
        sx={{
          position: "relative",
          flexShrink: 0,
          px: {
            xs: 2.25,
            sm: 3,
          },
          pt: 2.4,
          pb: 1.6,
          pr: 7,
          borderBottom: "none",
          backgroundColor: "var(--sear-superficie, #ffffff)",
        }}
      >
        <Typography
          component="h2"
          sx={{
            color: "var(--sear-azul, #0767d8)",
            fontSize: tamanhosModaisGlobais.tituloModal,
            fontWeight: 700,
            lineHeight: 1.15,
          }}
        >
          {editando ? "Editar SRE" : "Nova SRE"}
        </Typography>

        <Typography
          sx={{
            mt: 0.45,
            color: "var(--sear-texto-secundario, #64748b)",
            fontSize: tamanhosModaisGlobais.descricao,
            lineHeight: 1.4,
          }}
        >
          {editando
            ? "Atualize os dados, municípios vinculados ou os dados de acesso da Superintendência Regional de Ensino."
            : "Cadastre os dados, municípios vinculados e o acesso da Superintendência Regional de Ensino."}
        </Typography>

        <IconButton
          aria-label={editando ? "Fechar edição" : "Fechar cadastro"}
          onClick={fechar}
          disabled={salvando || salvandoSenha}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            color: "var(--sear-texto-secundario, #40597c)",

            "&:hover": {
              color: "var(--sear-azul, #0767d8)",
              backgroundColor: "var(--sear-superficie-azul, #eef6ff)",
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {dadosCarregados && (
        <Box
          sx={{
            flexShrink: 0,
            px: {
              xs: 0.5,
              sm: 1.25,
            },
            borderBottom: "1px solid var(--sear-borda, #e3edf6)",
            backgroundColor: "var(--sear-superficie, #ffffff)",
          }}
        >
          <Tabs
            value={abaAtiva}
            onChange={(_, novaAba) => {
              setAbaAtiva(novaAba);
              setErro("");
              setErroAcesso("");
              setSucessoAcesso("");
            }}
            variant="fullWidth"
            sx={{
              minHeight: 48,

              "& .MuiTab-root": {
                minHeight: 48,
                textTransform: "none",
                fontSize: tamanhosModaisGlobais.aba,
                fontWeight: 600,
                color: "var(--sear-texto-secundario, #60758f)",
                gap: 0.6,
                px: 1,
              },

              "& .MuiTab-iconWrapper": {
                mb: "0 !important",
              },

              "& .Mui-selected": {
                color: "var(--sear-azul, #0767d8) !important",
              },

              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
                backgroundColor: "var(--sear-acao-azul, #087cf0)",
              },
            }}
          >
            <Tab
              icon={<BuildingsFillIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Dados da SRE"
            />

            <Tab
              icon={<LocationCityRounded sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Cidades vinculadas"
            />

            <Tab
              icon={<LockResetRounded sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Dados de Acesso"
            />
          </Tabs>
        </Box>
      )}

      <Box
        component="form"
        onSubmit={abaAcessoEdicao ? salvarSenha : salvar}
        sx={{
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <DialogContent
          sx={{
            flex: 1,
            overflowY: "auto",
            px: {
              xs: 2,
              sm: 2.5,
            },
            py: 2.25,
            backgroundColor: "var(--sear-superficie, #fbfdff)",
          }}
        >
          {carregando ? (
            <Box
              sx={{
                minHeight: 300,
                display: "grid",
                placeItems: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : dadosCarregados ? (
            <>
              {abaAtiva === 0 && renderDadosSre()}

              {abaAtiva === 1 && renderCidades()}

              {editando && abaAtiva === 2 && renderDadosAcessoEdicao()}

              {!editando && abaAtiva === 2 && renderDadosAcessoCriacao()}
            </>
          ) : null}

          <FeedbackInline
            tipo="error"
            mensagem={erro}
            sx={{
              mt: 2,

              "& .MuiTypography-root": {
                fontSize: tamanhosModaisGlobais.textoAuxiliar,
              },
            }}
          />
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,
            justifyContent: "flex-end",
            gap: 1.25,
            px: {
              xs: 2,
              sm: 3,
            },
            py: 1.6,
            borderTop: "1px solid var(--sear-borda, #e3edf6)",
            backgroundColor: "var(--sear-superficie, #ffffff)",
            boxShadow: "0 -4px 12px rgba(20, 61, 106, 0.035)",
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={fechar}
            disabled={salvando || salvandoSenha}
            sx={{
              ...botaoCancelar,
              fontSize: tamanhosModaisGlobais.botao,
            }}
          >
            Cancelar
          </Button>

          <Button
            type="submit"
            variant="outlined"
            disabled={
              abaAcessoEdicao
                ? salvandoSenha || carregandoAcesso || !acesso
                : salvando || carregando || !dadosCarregados
            }
            sx={{
              ...botaoSalvar,
              fontSize: tamanhosModaisGlobais.botao,
              minWidth: {
                xs: 140,
                sm: 165,
              },
            }}
          >
            {abaAcessoEdicao
              ? salvandoSenha
                ? "Salvando..."
                : "Salvar alterações"
              : salvando
                ? editando
                  ? "Salvando..."
                  : "Criando..."
                : editando
                  ? "Salvar alterações"
                  : "Criar SRE"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
